import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useFormatters } from '../../shared/format/use-formatters';
import { usePlanPrices } from '../../shared/pricing/use-plan-prices';
import { Badge } from '../../shared/ui/badge';
import { ButtonLink } from '../../shared/ui/button';
import { sectionLeadClass, sectionTitleClass } from './landing-classes';
import { landingFaqQuestions } from './landing-faq';
import { LandingHeroCard } from './landing-hero-card';
import { landingSampleItem } from './landing-sample';
import { LandingStructuredData } from './landing-structured-data';
import { Pricing } from './pricing';

const steps = ['research', 'compare', 'track'] as const;

export const LandingPage = () => {
  const { t } = useTranslation();
  const { locale } = useParams();
  const { formatPrice } = useFormatters();
  const prices = usePlanPrices();
  const headlineLines = t('landing.hero.title').split(/(?<=\.) /);
  const { hits, known, price } = landingSampleItem;

  return (
    <div>
      <LandingStructuredData />
      <section
        className="grid items-start gap-12 py-12 sm:py-20 lg:grid-cols-[minmax(0,6fr)_minmax(0,6fr)]"
        aria-labelledby="landing-title"
      >
        <div>
          <h1
            id="landing-title"
            aria-label={t('landing.hero.title')}
            className="text-4xl font-semibold tracking-tight text-content sm:text-5xl lg:text-6xl"
          >
            {headlineLines.map((line) => (
              <span className="block" key={line}>
                {line}
              </span>
            ))}
          </h1>
          <p className="mt-6 max-w-[43ch] text-lg leading-relaxed text-muted">
            {t('landing.hero.description')}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6">
            <ButtonLink className="px-5 py-3 text-base" to={`/${locale}/sign-up`}>
              {t('landing.hero.primaryAction', prices)}
            </ButtonLink>
            <a
              className="text-base font-medium text-content underline decoration-line underline-offset-4 hover:decoration-accent"
              href="#how-it-works"
            >
              {t('landing.hero.secondaryAction')}
            </a>
          </div>
          <p className="mt-4 max-w-[43ch] text-sm text-muted">{t('landing.hero.offer', prices)}</p>
          <p className="mt-5 flex items-center gap-2 text-sm text-muted">
            <Badge>18+</Badge>
            {t('landing.hero.responsibility')}
          </p>
        </div>
        <div>
          <LandingHeroCard />
          <p className="mt-4 max-w-[55ch] text-sm leading-relaxed text-muted">
            {t('landing.hero.caption', { hits, known, price: formatPrice(price) })}
          </p>
        </div>
      </section>

      <section
        className="scroll-mt-20 border-t border-line py-12 sm:py-20"
        id="how-it-works"
        aria-labelledby="how-title"
      >
        <h2 className={sectionTitleClass} id="how-title">
          {t('landing.how.title')}
        </h2>
        <p className={sectionLeadClass}>{t('landing.how.description')}</p>
        <ol className="mt-10 grid gap-8 md:grid-cols-3">
          {steps.map((step) => (
            <li key={step}>
              <h3 className="text-lg font-semibold text-content">
                {t(`landing.how.steps.${step}.title`)}
              </h3>
              <p className="mt-2 max-w-[34ch] text-sm leading-relaxed text-muted">
                {t(`landing.how.steps.${step}.description`)}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section
        className="border-t border-line py-12 text-center sm:py-20"
        aria-labelledby="honesty-title"
      >
        <h2
          className="mx-auto max-w-[16ch] text-4xl font-semibold tracking-tight text-accent-ink sm:text-5xl lg:text-6xl"
          id="honesty-title"
        >
          {t('landing.honesty.title')}
        </h2>
        <p className="mx-auto mt-6 max-w-[60ch] text-lg text-muted">
          {t('landing.honesty.description')}
        </p>
      </section>

      <section
        className="grid gap-12 border-t border-line py-12 sm:py-20 lg:grid-cols-[1fr_1.6fr]"
        aria-labelledby="faq-title"
      >
        <h2 className={sectionTitleClass} id="faq-title">
          {t('landing.faq.title')}
        </h2>
        <div className="divide-y divide-line border-t border-line">
          {landingFaqQuestions.map((question, index) => (
            <details key={question} open={index === 0} className="group py-5">
              <summary className="flex cursor-pointer list-none items-baseline justify-between gap-4 font-semibold text-content [&::-webkit-details-marker]:hidden">
                {t(`landing.faq.items.${question}.question`)}
                <span aria-hidden="true" className="text-xl text-accent-ink group-open:hidden">
                  +
                </span>
                <span
                  aria-hidden="true"
                  className="hidden text-xl text-accent-ink group-open:inline"
                >
                  −
                </span>
              </summary>
              <p className="mt-3 max-w-prose text-muted">
                {t(`landing.faq.items.${question}.answer`, { product: t('app.name'), ...prices })}
              </p>
            </details>
          ))}
        </div>
      </section>

      <Pricing />

      <section
        className="mb-12 flex flex-col gap-6 border-t border-line py-12 sm:mb-16 sm:flex-row sm:items-center sm:justify-between sm:py-20"
        aria-labelledby="closing-title"
      >
        <div>
          <h2
            className="max-w-[22ch] text-2xl font-semibold tracking-tight text-content sm:text-3xl"
            id="closing-title"
          >
            {t('landing.closing.title', prices)}
          </h2>
          <p className="mt-3 max-w-[46ch] text-muted">{t('landing.closing.description', prices)}</p>
        </div>
        <ButtonLink className="shrink-0 px-5 py-3 text-base" to={`/${locale}/sign-up`}>
          {t('landing.hero.primaryAction', prices)}
        </ButtonLink>
      </section>
    </div>
  );
};
